import { api } from "./client";
import type { PrequalificationRole } from "./prequalification";

// Deal Flow → Prequalification → Program Brochure.
//
// Mirrors the C# records in Core/DTOs/Prequalification/ProgramBrochureDtos and
// the five routes on ProgramBrochuresController (camelCase wire; enums serialize
// as STRINGS platform-wide, so `role` is "Buyer" / "Seller" both ways). Every
// endpoint gates on Prequalification.ManageDocuments — the same permission as the
// Document Language Editor, deliberately reused.

/**
 * The upload ceiling, mirroring `ProgramBrochureService.MaxUploadBytes`.
 *
 * 🔴 It is 5 MB, not the platform's usual 25. The brochure is attached to the
 * applicant's completion email alongside their own generated documents, and that
 * email's combined-attachment budget is ALL-OR-NOTHING — an oversized brochure
 * would cost the applicant their prequalification letter. The service is the
 * authority and re-checks it; this constant exists so the person choosing a file
 * is told before 5 MB goes over the wire.
 */
export const PROGRAM_BROCHURE_MAX_BYTES = 5 * 1024 * 1024;

/** Mirrors `ProgramBrochureService.PdfContentType`. */
export const PROGRAM_BROCHURE_CONTENT_TYPE = "application/pdf";

/** `%PDF-`. Mirrors `ProgramBrochureService.PdfSignature` (ISO 32000 §7.5.2). */
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d];

/** One uploaded brochure — the active one, or a superseded one from history. */
export interface ProgramBrochure {
  id: string;
  role: PrequalificationRole;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  /** Best-effort, read from the PDF at upload. Null means UNKNOWN (the parser
   *  could not open the file), never zero — render it as absent. */
  pageCount: number | null;
  uploadedAtUtc: string;
  uploadedByUserId: string | null;
  isActive: boolean;
}

/** One role's brochure state. */
export interface ProgramBrochureRoleState {
  role: PrequalificationRole;
  /** Null when no brochure is active — a first-class state, not an error.
   *  Nothing is seeded and no PDF ships in the repository, so this is what both
   *  roles read on a fresh database. */
  active: ProgramBrochure | null;
  /** Superseded uploads only; the active row is NOT repeated here. Newest first. */
  history: ProgramBrochure[];
}

export interface ProgramBrochureOverview {
  buyer: ProgramBrochureRoleState;
  seller: ProgramBrochureRoleState;
}

/** A time-limited read link. `available: false` with null fields is the "no
 *  brochure on file" answer — deliberately data, not a 404. */
export interface ProgramBrochureLink {
  role: PrequalificationRole;
  available: boolean;
  url: string | null;
  expiresAtUtc: string | null;
  fileName: string | null;
}

const BASE = "/deal-flow/prequalification/program-brochures";

const roleSeg = (role: PrequalificationRole) => encodeURIComponent(role);

export const programBrochuresApi = {
  /** Both roles: the active brochure (or none) and every superseded upload. */
  overview: () => api.get<ProgramBrochureOverview>(BASE),

  /** Upload and make active. The previously active upload is retained as
   *  history — it is deactivated, never deleted. Returns the role's new state. */
  upload: (role: PrequalificationRole, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<ProgramBrochureRoleState>(`${BASE}/${roleSeg(role)}`, form);
  },

  /** Roll back to a prior upload. Idempotent when already active. */
  restore: (role: PrequalificationRole, id: string) =>
    api.post<ProgramBrochureRoleState>(`${BASE}/${roleSeg(role)}/restore/${encodeURIComponent(id)}`),

  /** Stop serving a brochure for this role. Rows and blobs are KEPT — this
   *  deactivates, and a restore brings the file back. */
  deactivate: (role: PrequalificationRole) =>
    api.delete<ProgramBrochureRoleState>(`${BASE}/${roleSeg(role)}`),

  /** A time-limited read link to the active brochure, for the admin's preview.
   *  Deliberately NOT cached by the caller — a SAS expires. */
  link: (role: PrequalificationRole) =>
    api.get<ProgramBrochureLink>(`${BASE}/${roleSeg(role)}/link`),
};

// ── client-side validation ─────────────────────────────────────────────────
//
// A MIRROR of ProgramBrochureService's four checks, in the service's own order,
// so the reasons a person is given before uploading are the reasons the server
// would give afterwards. The server remains the authority and re-runs all four;
// this exists because the alternative is sending up to 5 MB to be told the file
// was the wrong type, and because the pipeline-level [RequestSizeLimit] returns a
// bare 413 with no JSON envelope for the client to read a message out of.

/** Read the first `count` bytes of a file.
 *
 *  Two routes on purpose. `Blob.arrayBuffer()` is the modern one; jsdom 25 does
 *  NOT implement it (verified), so a signature check written only that way would
 *  be dead in every test — passing for the wrong reason, or refusing every
 *  fixture. `FileReader` works in jsdom and in every browser, so it is the
 *  fallback. Returns null when neither is available, which the caller treats as
 *  "could not check" rather than "not a PDF". */
async function readLeadingBytes(file: File, count: number): Promise<Uint8Array | null> {
  const slice = typeof file.slice === "function" ? file.slice(0, count) : file;

  if (typeof (slice as Blob).arrayBuffer === "function") {
    try {
      return new Uint8Array(await (slice as Blob).arrayBuffer());
    } catch {
      /* fall through to FileReader */
    }
  }

  if (typeof FileReader === "function") {
    try {
      return await new Promise<Uint8Array | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(
            reader.result instanceof ArrayBuffer ? new Uint8Array(reader.result) : null,
          );
        reader.onerror = () => resolve(null);
        reader.readAsArrayBuffer(slice);
      });
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Why this file cannot be uploaded, or null when it passes every check the
 * client can run.
 *
 * 🔴 FAIL-OPEN on the signature check alone. If the bytes cannot be read — an
 * environment with neither `Blob.arrayBuffer` nor `FileReader` — the upload
 * proceeds and the server's own signature check decides. A client-side check
 * that cannot run must never refuse a file the server would accept; the three
 * checks above it need no bytes and always run.
 */
export async function describeInvalidBrochureFile(file: File): Promise<string | null> {
  // 1. Mirrors `sizeBytes <= 0`.
  if (file.size <= 0) return "That file is empty. Choose the brochure PDF.";

  // 2. Mirrors the MaxUploadBytes check, and carries the same reason the server
  //    gives — the limit is a consequence of the email budget, not an arbitrary
  //    number, and an admin told only "too large" would reasonably ask for more.
  if (file.size > PROGRAM_BROCHURE_MAX_BYTES) {
    return (
      `That file is ${formatBrochureSize(file.size)}. The maximum is 5 MB, because the ` +
      "brochure is emailed to the applicant alongside their prequalification letter and " +
      "deal sheet, and an oversized attachment set would drop those documents from the email."
    );
  }

  // 3. Mirrors the declared-content-type check. `File.type` is what the browser
  //    puts on the multipart part, which is exactly what IFormFile.ContentType
  //    reads — so this refuses precisely what the server would.
  if (file.type.toLowerCase() !== PROGRAM_BROCHURE_CONTENT_TYPE) {
    const declared = file.type ? `'${file.type}'` : "an unrecognized type";
    return `That file is ${declared}. Upload a PDF (application/pdf).`;
  }

  // 4. Mirrors `RequirePdfSignature`. A renamed .docx and a PDF-labelled image
  //    both pass every check above and fail here.
  const head = await readLeadingBytes(file, PDF_SIGNATURE.length);
  if (head === null) return null;
  if (
    head.length < PDF_SIGNATURE.length ||
    PDF_SIGNATURE.some((byte, i) => head[i] !== byte)
  ) {
    return (
      "That file is not a PDF. Its content does not begin with the PDF signature " +
      "(%PDF-), whatever its name or declared type says."
    );
  }

  return null;
}

/** Byte size for display. Matches the shape used by the DMS and knowledge-base
 *  upload surfaces; there is no shared formatter in the design system. */
export function formatBrochureSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
